import {
  ECheckLifecycleStatus,
  ECheckType,
  EResultStatus,
  ICheckResult
} from '@publishing-engine/domain/models';

export type IIntegrityTally = {
  risk: number;
  warning: number;
  passed: number;
};

/**
 * What the author panel has to say about a submission, and what the submit
 * button should do about it.
 *
 * `ok` covers "nothing is configured for this role" as well as "the checks ran
 * and found no risk" — an author is only ever held back by a risk the checks
 * actually reported.
 */
export type EIntegrityGate = 'ok' | 'not-run' | 'running' | 'risks';

/**
 * Counts checks, not the occurrences under them.
 *
 * Each check is classified once, by the status the vendor put on the check
 * itself, so ten red occurrences in one check are still a single risk. A check
 * therefore lands in exactly one tally and the three tiles add up to the number
 * of checks that reported a status.
 */
export const tallyChecks = (checks: ICheckResult[]): IIntegrityTally => {
  const tally: IIntegrityTally = { risk: 0, warning: 0, passed: 0 };

  checks.forEach(check => {
    switch (check.result?.status) {
      case EResultStatus.RED:
        tally.risk += 1;
        break;
      case EResultStatus.ORANGE:
      case EResultStatus.BROWN:
        tally.warning += 1;
        break;
      case EResultStatus.GREEN:
        tally.passed += 1;
        break;
      default:
        break;
    }
  });

  return tally;
};

/**
 * A detailed report is generated through a `decision_maker`-only endpoint, so
 * the shared check list would offer an author a link that can only fail. Drop
 * the flag rather than let it through.
 */
export const toAuthorView = (checks: ICheckResult[]): ICheckResult[] =>
  checks.map(check =>
    check.result?.showDetailedReport
      ? { ...check, result: { ...check.result, showDetailedReport: false } }
      : check
  );

/**
 * `undefined` — no result row for the check yet — counts as pending: a run that
 * has not written anything must keep the panel waiting rather than report that
 * it finished.
 */
const isPendingStatus = (status?: ECheckLifecycleStatus) =>
  status === undefined ||
  status === ECheckLifecycleStatus.NOT_STARTED ||
  status === ECheckLifecycleStatus.PROCESSING;

/**
 * A run is over when no row is still queued or with the vendor. Rows are the
 * only source of truth: the run endpoint answers with the vendor's job id,
 * which nothing can be read back with.
 */
export const isRunInProgress = (checks: ICheckResult[]): boolean =>
  checks.some(check => isPendingStatus(check.status));

export const getIntegrityGate = (
  checks: ICheckResult[],
  configuredChecks: ECheckType[]
): EIntegrityGate => {
  if (!configuredChecks.length) return 'ok';
  if (!checks.length) return 'not-run';
  if (isRunInProgress(checks)) return 'running';

  return tallyChecks(checks).risk > 0 ? 'risks' : 'ok';
};

/**
 * Whether the gate should hold the submission back.
 *
 * A result set the service would not hand over is not evidence that the checks
 * failed, and the query does not retry, so failing closed would let one flaky
 * request lock an author out of submitting. Risk is only ever acted on when it
 * was actually read.
 */
export const shouldBlockSubmit = (
  gate: EIntegrityGate,
  hasError: boolean
): boolean => !hasError && gate !== 'ok';

/**
 * The run endpoint refuses a second run while one is in flight and says so in
 * a body that reaches us as either a string or an error object.
 */
export const runErrorMessage = (message: unknown): string => {
  if (typeof message === 'string' && message.trim()) return message;

  if (message && typeof message === 'object' && 'message' in message) {
    const inner = (message as { message?: unknown }).message;
    if (typeof inner === 'string' && inner.trim()) return inner;
  }

  return 'Please try again later.';
};
